/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Utils for claim information
 */
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import { renderDate } from 'components/eligibility/response/RenderSchema';
import { getClaimStatus } from 'utils/parseFhirDataType';
import { schemaKeys } from 'utils/claims/adjudicationSchema';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { CLAIM_WORK_STATES, GROUPED_CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { CLAIM_STATUS_CODES_FOR_POSTING, POST_PAYMENT_STATE } from 'app-constants/claims';
import {
	ADJUSTMENT_GROUP_CODES,
	PATIENT_RESPONSIBILITIES_GROUP_CODES,
	BILLED_AMOUNT_CODES,
	PAID_AMOUNT_CODES,
} from 'app-constants/claims/reasonCodes';
import { CLAIM_STATUS_CODES } from 'app-constants/claims/claimStatusCodes';
import { MODAL_COPY, DRAFT_CLAIM_BANNER_MESSAGE } from 'app-constants/claims/postPaymentConfig';

import { fromCents, toCents } from './postPayment';

const MODAL_TITLE = 'Confirmation';

const CLAIM_RESPONSE_TOTAL_CODING = {
	patientResponsibility: {
		code: 'patient-responsibility',
		system: fhirSystemMapping.AdjudicationCodes,
	},
	billedAmount: {
		code: 'submitted',
		system: fhirSystemMapping.AdjudicationCodes,
	},
};

const CLAIM_SERVICE_LINE_CODING = {
	billedAmount: {
		code: 'submitted',
		system: fhirSystemMapping.AdjudicationCodes,
	},
	allowedAmount: {
		code: 'B6',
		system: fhirSystemMapping['AMT-01'],
	},
	paidAmount: {
		code: 'benefit',
		system: fhirSystemMapping.AdjudicationCodes,
		display: 'Benefit Amount',
	},
	patientResponsibility: {
		code: 'PR',
		system: fhirSystemMapping['CAS-01'],
	},
	adjustedAmount: {
		code: 'CO',
		system: fhirSystemMapping['CAS-01'],
	},
};

const PAID_OR_FINALIZED_STATUS_CODES = _.chain(CLAIM_STATUS_CODES)
	.filter(({ label }) => _.includes(CLAIM_STATUS_CODES_FOR_POSTING, label))
	.map('code')
	.value();

const isClaimPendingOrPaid = (claimResponse?: Fhir.ClaimResponse) => {
	const claimStatus = getClaimStatus(claimResponse?.decision || claimResponse?.payment?.type);
	return claimStatus ? _.includes(PAID_OR_FINALIZED_STATUS_CODES, claimStatus) : false;
};

type IPostPaymentMetaProps = {
	claim: ClaimRequest.IClaim;
	hasManualClaim: boolean;
	eraClaimsCount: number;
	isClaimStatusSupported: boolean;
	paymentPostedDate?: string;
	eraReceivedDate?: string;
	hasDraftClaims?: boolean;
};

const getPostPaymentMeta = ({
	claim,
	hasManualClaim,
	eraClaimsCount,
	isClaimStatusSupported,
	paymentPostedDate,
	eraReceivedDate,
	hasDraftClaims,
}: IPostPaymentMetaProps): { postPaymentState: ClaimStatusResponse.IPostPaymentState; tagMessage: string } => {
	const workState = claim?.metadata?.workState;
	const responses = claim?.fhirClaimResponses;
	const isAutoPostedClaimAvailable = !!responses?.post_era;
	const isManualPostedClaimAvailable = !!responses?.post_manual;
	const isClaimActive = _.includes(GROUPED_CLAIM_WORK_STATES.active.value, workState);
	const isPaymentPostedWorkState = workState === CLAIM_WORK_STATES.a_payment_posted.value;

	// TODO : later revert to data?.fhirClaimResponses?.x12_277
	// TODO : later revert to responses?.x12_277 as Fhir.ClaimResponse
	const isEraPending =
		isClaimPendingOrPaid(claim?.claimStatusRequestResponse?.fhirClaimResponse as Fhir.ClaimResponse) && isClaimStatusSupported;

	let postPaymentState: ClaimStatusResponse.IPostPaymentState;

	if (isPaymentPostedWorkState) {
		postPaymentState = isAutoPostedClaimAvailable ? POST_PAYMENT_STATE.autoPosted : POST_PAYMENT_STATE.manualPosted;
	} else if (!isClaimActive) {
		postPaymentState = POST_PAYMENT_STATE.closedClaim;
	} else if (hasManualClaim) {
		postPaymentState =
			isPaymentPostedWorkState || isManualPostedClaimAvailable ? POST_PAYMENT_STATE.manualPosted : POST_PAYMENT_STATE.draftClaim;
	} else if (eraClaimsCount > 1) {
		postPaymentState = POST_PAYMENT_STATE.multiplePayments;
	} else if (eraClaimsCount === 1) {
		postPaymentState = POST_PAYMENT_STATE.eraClaim;
	} else if (isEraPending) {
		postPaymentState = POST_PAYMENT_STATE.pendingEra;
	} else {
		postPaymentState = POST_PAYMENT_STATE.emptyClaim;
	}

	const formatPostedMessage = (label: string, date?: string) => {
		return date ? `${label} on ${renderDate(date)}` : label;
	};

	const messageMap = {
		[POST_PAYMENT_STATE.autoPosted]: formatPostedMessage('Payment auto-posted', paymentPostedDate),
		[POST_PAYMENT_STATE.manualPosted]: formatPostedMessage('Payment manually posted', paymentPostedDate),
		[POST_PAYMENT_STATE.draftClaim]: 'Manual Payment Posting in Progress. Select "Post Payment" to continue and update the claim.',
		[POST_PAYMENT_STATE.multiplePayments]:
			'Multiple insurance payment records (CLPs) were found. Select "Post Payment" to choose which records to post.',
		[POST_PAYMENT_STATE.eraClaim]: eraReceivedDate
			? `Insurance Payment Report (ERA) received on ${renderDate(eraReceivedDate)}`
			: 'Insurance Payment Report (ERA) received.',
		[POST_PAYMENT_STATE.pendingEra]:
			'Insurance Payment Report (ERA) not available yet. Select "Post Payment" to enter payment details manually, or wait for the Insurance Payment Report (ERA) to be ingested.',
		[POST_PAYMENT_STATE.emptyClaim]:
			'No Insurance Payment Reports (ERAs) found for this claim. Select "Post Payment" to enter payment details manually.',
	};

	return {
		postPaymentState,
		tagMessage: hasDraftClaims ? DRAFT_CLAIM_BANNER_MESSAGE : (messageMap[postPaymentState] ?? ''),
	};
};

const getPostPaymentState = (props: IPostPaymentMetaProps): ClaimStatusResponse.IPostPaymentState => {
	const { postPaymentState } = getPostPaymentMeta(props);
	return postPaymentState;
};

type IProps = {
	data: ClaimRequest.IClaim;
};

const getPaymentPostingMeta = ({ data }: IProps): { isPaymentPosted: boolean; isManualPosted: boolean; paymentPostedDate: string } => {
	const paymentAutoPosted = data?.fhirClaimResponses?.post_era;
	const paymentManualPosted = data?.fhirClaimResponses?.post_manual;

	const paymentPostedDate = paymentAutoPosted ? paymentAutoPosted?.created : paymentManualPosted?.created;

	return {
		isPaymentPosted: !!(paymentAutoPosted || paymentManualPosted),
		isManualPosted: !!paymentManualPosted,
		paymentPostedDate,
	};
};

const getAdjustedAmountCents = (data: Fhir.IClaimResponseItemAdjudication[]): number => {
	return _.sumBy(data, ({ category, amount }) =>
		_.includes(ADJUSTMENT_GROUP_CODES, category?.coding?.[0]?.code) ? toCents(amount?.value || 0) : 0,
	);
};

const getPatientResponsibilityCents = (data: Fhir.IClaimResponseItemAdjudication[]): number => {
	return _.sumBy(data, ({ category, amount }) =>
		_.includes(PATIENT_RESPONSIBILITIES_GROUP_CODES, category?.coding?.[0]?.code) ? toCents(amount?.value || 0) : 0,
	);
};
const getBilledAmountCents = (data: Fhir.IClaimResponseItemAdjudication[]): number => {
	return _.sumBy(data, ({ category, amount }) =>
		_.includes(BILLED_AMOUNT_CODES, category?.coding?.[0]?.code) ? toCents(amount?.value || 0) : 0,
	);
};

const getPaidAmountCents = (data: Fhir.IClaimResponseItemAdjudication[]): number => {
	return _.sumBy(data, ({ category, amount }) =>
		_.includes(PAID_AMOUNT_CODES, category?.coding?.[0]?.code) ? toCents(amount?.value || 0) : 0,
	);
};

const getClaimResponseFinancialMeta = (
	data: Fhir.ClaimResponse,
): {
	patientResponsibilityAmount: number | null;
	billedAmount: number | null;
	paidAmount: number | null;
	payerAdjusted: number | null;
} => {
	if (!data) {
		return {
			patientResponsibilityAmount: null,
			billedAmount: null,
			paidAmount: null,
			payerAdjusted: null,
		};
	}
	const totalAmount = data?.total;
	let patientResponsibilityAmount = null;
	let billedAmount = null;
	let calculatedAdjustedCents = null;
	let calculatedPRCents = null;
	let calculatedBilledCents = 0;
	let calculatedPaidCents = 0;

	_.forEach(data?.addItem, ({ adjudication, net }) => {
		calculatedAdjustedCents += getAdjustedAmountCents(adjudication);
		calculatedPRCents += getPatientResponsibilityCents(adjudication);
		calculatedBilledCents += getBilledAmountCents(adjudication) ?? toCents(net?.value);
		calculatedPaidCents += getPaidAmountCents(adjudication);
	});

	calculatedAdjustedCents += getAdjustedAmountCents(data?.adjudication);
	calculatedPRCents += getPatientResponsibilityCents(data?.adjudication);

	_.forEach(totalAmount, ({ category, amount }) => {
		const patientResponsibilityCoding = _.find(
			category?.coding,
			(coding) =>
				coding?.code === CLAIM_RESPONSE_TOTAL_CODING.patientResponsibility.code &&
				coding?.system === CLAIM_RESPONSE_TOTAL_CODING.patientResponsibility.system,
		);
		const billedAmountCoding = _.find(
			category?.coding,
			(coding) =>
				coding?.code === CLAIM_RESPONSE_TOTAL_CODING.billedAmount.code &&
				coding?.system === CLAIM_RESPONSE_TOTAL_CODING.billedAmount.system,
		);

		if (patientResponsibilityCoding) {
			patientResponsibilityAmount = amount?.value;
		}
		if (billedAmountCoding) {
			billedAmount = amount?.value;
		}
	});

	const paidAmount = data?.payment?.amount?.value ?? (calculatedPaidCents > 0 ? fromCents(calculatedPaidCents) : null);
	const payerAdjusted = data?.payment?.adjustment?.value ?? fromCents(calculatedAdjustedCents);
	const patientResponsibility = patientResponsibilityAmount ?? fromCents(calculatedPRCents);

	return {
		patientResponsibilityAmount: patientResponsibility ?? 0,
		billedAmount: billedAmount ?? (calculatedBilledCents > 0 ? fromCents(calculatedBilledCents) : 0),
		paidAmount: paidAmount ?? 0,
		payerAdjusted: payerAdjusted ?? 0,
	};
};

const getEraResponses = (claimResponsesData?: ClaimResponse.IClaimResponseResult[]): ClaimResponse.IClaimResponseResult[] => {
	return _.filter(claimResponsesData, ({ source }) => source === 'x12_835' || source === 'eob');
};

const getLatestDate = (dates: (string | undefined)[]): string | undefined => {
	return _.chain(dates)
		.compact()
		.filter((date) => dateParser(date).isValid())
		.maxBy((date) => dateParser(date).valueOf())
		.value();
};

const getEraMeta = ({
	claimResponsesData,
	data,
}: {
	claimResponsesData: ClaimResponse.IClaimResponseResult[];
	data: ClaimRequest.IClaim;
}): { eraReceivedDate?: string; hasEraResponse: boolean; pendingPR: number } => {
	const eraResponses = getEraResponses(claimResponsesData);
	const eraReceivedDate = getLatestDate(_.map(eraResponses, ({ fhirResource }) => fhirResource?.created ?? fhirResource?.payment?.date));

	return {
		eraReceivedDate,
		hasEraResponse: !_.isEmpty(eraResponses),
		pendingPR: data?.metadata?.pendingPR ?? 0,
	};
};

const getPostingModalProps = ({
	isAutoPosting,
	hasPendingPR,
}: {
	isAutoPosting: boolean;
	hasPendingPR: boolean;
}): { title: string; content: string } => {
	const copyGroup = isAutoPosting ? MODAL_COPY.AUTO_POST : MODAL_COPY.MANUAL_POST;
	const content = hasPendingPR ? copyGroup.withPendingPatientResponsibility : copyGroup.withZeroPatientResponsibility;

	return {
		title: MODAL_TITLE,
		content,
	};
};

const isPaymentSummaryRowEmpty = (rowData?: ClaimStatusResponse.IERATableData): boolean => {
	if (!rowData) {
		return true;
	}

	const amountsAreZero = _.every(
		[schemaKeys.billedAmount, schemaKeys.paidAmount, schemaKeys.adjustedAmount, schemaKeys.patientResponsibility],
		(key) => _.toNumber(rowData?.[key]) === 0,
	);

	const paymentDate = rowData?.paymentDate;
	const eftChequeNo = rowData?.eftChequeNo;
	const clearingHouse = rowData?.clearingHouse;
	const eraNumber = rowData?.eraNumber;

	return !paymentDate && !eftChequeNo && !clearingHouse && !eraNumber && amountsAreZero;
};

const getTagMessage = (props: IPostPaymentMetaProps): string => {
	const { tagMessage } = getPostPaymentMeta(props);
	return tagMessage;
};

const VOID_CLAIM_MESSAGES = {
	voidFiled: 'The void request for this claim has been successfully submitted and is awaiting payer adjudication.',
	primaryClaimVoided:
		'The primary claim associated with this secondary has been voided. This claim can only proceed with a void request; other actions are not available.',
	voidSuperseded: 'The void request for this claim has been successfully submitted.',
};

export {
	CLAIM_RESPONSE_TOTAL_CODING,
	CLAIM_SERVICE_LINE_CODING,
	VOID_CLAIM_MESSAGES,
	getPostPaymentMeta,
	getPostPaymentState,
	getClaimResponseFinancialMeta,
	getPaymentPostingMeta,
	getEraMeta,
	getPostingModalProps,
	getTagMessage,
	isPaymentSummaryRowEmpty,
	getEraResponses,
};
